import React, { useEffect, useState } from 'react';
import { CalendarClock, History, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiPost } from '../../lib/api';

const DEADLINES = [
  { key: 'technical_submission_deadline', type: 'technical', label: 'Technical Offer', color: 'text-sky-700' },
  { key: 'commercial_submission_deadline', type: 'commercial', label: 'Commercial Offer', color: 'text-violet-700' },
  { key: 'submission_deadline', type: 'submission', label: 'Submission (default)', color: 'text-rose-700' },
];

const lbl = 'block text-[11px] font-display font-bold uppercase tracking-wider text-slate-500';
const inp = 'mt-1 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-[#3b4872] outline-none';

export default function EnquiryDeadlinesPanel({ enqId, enq, onUpdated }) {
  const [dates, setDates] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [extendFor, setExtendFor] = useState(null); // {type, label, current}
  const [extForm, setExtForm] = useState({ new_date: '', client_approval_ref: '', notes: '' });

  useEffect(() => {
    setDates({
      technical_submission_deadline: (enq.technical_submission_deadline || '').slice(0, 10),
      commercial_submission_deadline: (enq.commercial_submission_deadline || '').slice(0, 10),
      submission_deadline: (enq.submission_deadline || '').slice(0, 10),
    });
  }, [enq]);

  const loadHistory = async () => {
    try { const r = await apiGet(`/enquiries/${enqId}/deadline-extensions`); setHistory(r.items || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { if (enqId) loadHistory(); }, [enqId]);

  const saveDates = async () => {
    setSaving(true);
    try {
      await apiPut(`/enquiries/${enqId}`, dates);
      toast.success('Deadlines saved');
      onUpdated && onUpdated();
    } catch { toast.error('Could not save deadlines'); }
    finally { setSaving(false); }
  };

  const submitExtension = async () => {
    if (!extForm.new_date) { toast.error('Pick a new date'); return; }
    try {
      await apiPost(`/enquiries/${enqId}/extend-deadline`, {
        deadline_type: extendFor.type,
        new_date: extForm.new_date,
        client_approval_ref: extForm.client_approval_ref,
        notes: extForm.notes,
      });
      toast.success(`${extendFor.label} deadline extended`);
      setExtendFor(null);
      setExtForm({ new_date: '', client_approval_ref: '', notes: '' });
      await loadHistory();
      onUpdated && onUpdated();
    } catch { toast.error('Could not extend deadline'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-5" data-testid="enq-deadlines-panel">
      <div className="flex items-center gap-2">
        <CalendarClock size={16} className="text-[#3b4872]" />
        <h3 className="font-display font-bold uppercase tracking-wide text-sm">Deadlines</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {DEADLINES.map((d) => (
          <div key={d.key}>
            <label className={`${lbl} ${d.color}`}>{d.label}</label>
            <input
              type="date"
              value={dates[d.key] || ''}
              onChange={(e) => setDates((s) => ({ ...s, [d.key]: e.target.value }))}
              className={inp}
              data-testid={`enq-deadline-${d.type}`}
            />
            <button
              onClick={() => { setExtendFor({ type: d.type, label: d.label, current: dates[d.key] }); setExtForm({ new_date: '', client_approval_ref: '', notes: '' }); }}
              className="mt-1.5 text-[11px] text-[#3b4872] hover:underline font-display font-bold uppercase tracking-wider"
              data-testid={`enq-extend-${d.type}-btn`}
            >
              Extend deadline
            </button>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button onClick={saveDates} disabled={saving} data-testid="enq-deadlines-save" className="px-4 py-2 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wide rounded-md hover:bg-[#2c3658] disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Deadlines'}
        </button>
      </div>

      {/* Extension history */}
      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <History size={13} className="text-slate-500" />
          <span className="text-[11px] font-display font-bold uppercase tracking-wider text-slate-500">Extension History</span>
        </div>
        {history.length === 0 ? (
          <p className="text-xs text-slate-400" data-testid="enq-ext-history-empty">No extensions recorded.</p>
        ) : (
          <ul className="space-y-2" data-testid="enq-ext-history">
            {history.map((h) => (
              <li key={h.id} className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <span className="font-display font-bold uppercase tracking-wider text-[#3b4872]">{h.deadline_type}</span>
                {' · '}<span className="line-through text-slate-400">{(h.old_date || '—').slice(0, 10)}</span>
                {' → '}<span className="font-semibold">{(h.new_date || '').slice(0, 10)}</span>
                {h.client_approval_ref ? <span className="ml-2 text-slate-500">Ref: {h.client_approval_ref}</span> : null}
                <div className="text-[11px] text-slate-500 mt-0.5">By {h.requested_by || '—'} · {(h.approved_at || '').slice(0, 10)}{h.notes ? ` · ${h.notes}` : ''}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Extend dialog */}
      {extendFor && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setExtendFor(null)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-md shadow-2xl z-50 p-5" data-testid="enq-extend-dialog">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2"><Plus size={14} /> Extend {extendFor.label}</h4>
              <button onClick={() => setExtendFor(null)}><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Current: <span className="font-mono">{extendFor.current || '— not set —'}</span></p>
            <div className="space-y-3">
              <div>
                <label className={lbl}>New Date *</label>
                <input type="date" value={extForm.new_date} onChange={(e) => setExtForm((s) => ({ ...s, new_date: e.target.value }))} className={inp} data-testid="enq-extend-newdate" />
              </div>
              <div>
                <label className={lbl}>Client Approval Reference</label>
                <input type="text" value={extForm.client_approval_ref} onChange={(e) => setExtForm((s) => ({ ...s, client_approval_ref: e.target.value }))} placeholder="Email ref / letter no / doc id" className={inp} data-testid="enq-extend-ref" />
              </div>
              <div>
                <label className={lbl}>Notes</label>
                <textarea value={extForm.notes} onChange={(e) => setExtForm((s) => ({ ...s, notes: e.target.value }))} rows={2} className={inp} data-testid="enq-extend-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setExtendFor(null)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={submitExtension} className="px-4 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wide rounded-md" data-testid="enq-extend-submit">Record Extension</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
